/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Purpose } from '../../managers/sso';
import type { OtpMountBehaviour } from './types';

export type EmailFlowKey =
  | 'passwordless'
  | 'login2fa'
  | 'delete'
  | 'phoneChange'
  | 'purpose'
  | 'emailChange';

export interface OtpPurposeEntry<TWorker extends string> {
  worker: TWorker;
  behaviour: OtpMountBehaviour;
}

export const EMAIL_OTP_PURPOSE_CONFIG: Record<
  Purpose,
  OtpPurposeEntry<EmailFlowKey>
> = {
  [Purpose.EMAIL_OTP_VERIFICATION_LOGIN]: {
    worker: 'passwordless',
    behaviour: 'start-timer',
  },
  [Purpose.EMAIL_OTP_VERIFICATION_SIGNUP]: {
    worker: 'passwordless',
    behaviour: 'start-timer',
  },
  [Purpose.DELETE_ACCOUNT]: { worker: 'delete', behaviour: 'request' },
  [Purpose.PHONE_CHANGE]: { worker: 'phoneChange', behaviour: 'request' },
  [Purpose.PASSWORD_CHANGE]: { worker: 'purpose', behaviour: 'start-timer' },
  [Purpose.FORGOT_PASSWORD]: { worker: 'purpose', behaviour: 'start-timer' },
  [Purpose.LOGIN_2FA]: { worker: 'login2fa', behaviour: 'start-timer' },
  [Purpose.UPDATE_2FA]: { worker: 'purpose', behaviour: 'request' },
  [Purpose.USERNAME_CHANGE]: {
    worker: 'emailChange',
    behaviour: 'start-timer',
  },
  [Purpose.PHONE_VERIFICATION]: {
    worker: 'emailChange',
    behaviour: 'start-timer',
  },
};

export type PhoneFlowKey =
  | 'phoneVerification'
  | 'login2fa'
  | 'phoneChange'
  | 'emailChangeOnPhone';

export const PHONE_OTP_PURPOSE_CONFIG: Record<
  Purpose,
  OtpPurposeEntry<PhoneFlowKey>
> = {
  // The screen sends the code itself: callers arrive here cold from the sign-up
  // form, from "update mobile number", and from the host's verify-account
  // dialog, so making each of them send first left two of the three silent.
  [Purpose.PHONE_VERIFICATION]: {
    worker: 'phoneVerification',
    behaviour: 'request',
  },
  [Purpose.LOGIN_2FA]: { worker: 'login2fa', behaviour: 'start-timer' },
  [Purpose.PHONE_CHANGE]: { worker: 'phoneChange', behaviour: 'start-timer' },
  [Purpose.USERNAME_CHANGE]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
  [Purpose.EMAIL_OTP_VERIFICATION_LOGIN]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
  [Purpose.EMAIL_OTP_VERIFICATION_SIGNUP]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
  [Purpose.PASSWORD_CHANGE]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
  [Purpose.DELETE_ACCOUNT]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
  [Purpose.UPDATE_2FA]: { worker: 'emailChangeOnPhone', behaviour: 'request' },
  [Purpose.FORGOT_PASSWORD]: {
    worker: 'emailChangeOnPhone',
    behaviour: 'request',
  },
};

const PURPOSE_VALUES = new Set<string>(Object.values(Purpose));

export function parsePurposeParam(
  value: string | undefined,
  fallback: Purpose,
): Purpose {
  return value && PURPOSE_VALUES.has(value) ? (value as Purpose) : fallback;
}
